import { useState } from 'react';
import { signup } from '../api/signup';
import type { SignupFormData, SignupError } from '../types';

interface UseSignupReturn {
  isLoading: boolean;
  isSuccess: boolean;
  successMessage: string;
  errors: SignupError;
  handleSignup: (formData: SignupFormData) => Promise<void>;
  clearError: (field: string) => void;
}

export const useSignup = (): UseSignupReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState<SignupError>({});

  const clearError = (field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleSignup = async (formData: SignupFormData): Promise<void> => {
    setIsLoading(true);
    setErrors({});
    setIsSuccess(false);

    try {
      const signupPayload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber,
        job_title: formData.jobTitle,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
      };

      const response = await signup(signupPayload);

      // Store the token if needed
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }

      // Set success state - NO redirect, NO toast
      setIsSuccess(true);
      setSuccessMessage(response.message || 'Please check your email to verify your account.');
    } catch (error: any) {
      console.error('Signup error:', error);

      // Handle validation errors from the API
      if (error?.data?.errors) {
        const apiErrors = error.data.errors;
        // Convert snake_case API error keys to camelCase for frontend
        const formattedErrors: SignupError = {};
        
        Object.keys(apiErrors).forEach((key) => {
          // Convert snake_case to camelCase
          const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
          formattedErrors[camelKey] = apiErrors[key];
        });
        
        setErrors(formattedErrors);
      } else {
        // Handle generic errors
        const errorMessage = error?.data?.error || error?.message || 'An error occurred during signup. Please try again.';
        setErrors({ non_field_errors: [errorMessage] });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSuccess,
    successMessage,
    errors,
    handleSignup,
    clearError,
  };
};

